"use client";

/**
 * Responsive Modal Component
 * 
 * Uses the Standardized Drawer System for mobile responsiveness.
 * For comprehensive drawer usage guide, examples, and best practices, see:
 * 📖 apps/hub/docs/ui-components/STANDARDIZED_DRAWER_README.md
 */

import * as React from "react";
import { Modal } from '@mantine/core';
import { cn } from "../../lib/utils.js";

// Import types
import type { ResponsiveModalProps } from "./types.js";

// Import hooks
import {
  useResponsiveModalState,
  useResponsiveModalLayout,
  useResponsiveModalHandlers,
  useResponsiveModalStyles,
} from "./hooks/index.js";

// Import sub-components
import { ResponsiveModalDrawer } from "./ResponsiveModalDrawer.js";
import { ResponsiveModalContent } from "./ResponsiveModalContent.js";

// Import utilities
import { getModalStackProps } from "./utils/modalProps.js";

export const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  globalModalId,
  open: propOpen,
  onOpenChange: propOnOpenChange,
  title,
  description,
  children,
  className = "",
  maxWidth = "2xl",
  side = "bottom",
  showCloseButton = true,
  mobileBreakpoint = "(max-width: 768px)",
  forceModal = false,
  forceDrawer = false,
  draggable = false,
  stickySubmit,
  showDiagnosticButton = false,
  diagnosticMetadata = {},
  onCreateTask,
  loading = false,
  error,
  showLoadingOverlay = false,
  loadingComponent,
  preventCloseWhenLoading = false,
  headerContent,
}) => {
  // Get state management (global stack or controlled)
  const { open, onOpenChange, useGlobalStack, globalModalStack, globalDrawerStack } = 
    useResponsiveModalState(globalModalId, propOpen, propOnOpenChange);

  // Get responsive layout logic
  const { shouldUseDrawer } = useResponsiveModalLayout(
    forceModal,
    forceDrawer,
    open,
    globalDrawerStack,
    globalModalId,
    side,
    title,
    description,
    showCloseButton
  );

  // Get event handlers
  const { handleClose } = useResponsiveModalHandlers(
    onOpenChange,
    preventCloseWhenLoading,
    loading
  );

  // Get styles and sizing
  const { mantineSize, rootStyleOverride, classNames, headerClassNames, is90vw, isFull } =
    useResponsiveModalStyles(maxWidth, className, !!headerContent);

  // Render drawer for mobile
  if (shouldUseDrawer) {
    return (
      <ResponsiveModalDrawer
        drawerId={globalModalId!}
        side={side}
        className={className}
        title={title}
        description={description}
        showCloseButton={showCloseButton}
        stickySubmit={stickySubmit}
      >
        {children}
      </ResponsiveModalDrawer>
    );
  }

  // Get modal registration props for global stack
  const modalProps = getModalStackProps(useGlobalStack, globalModalId, globalModalStack);

  // Render modal for desktop
  return (
    <Modal
      {...modalProps}
      opened={open}
      onClose={handleClose}
      centered={!isFull}
      fullScreen={isFull}
      size={mantineSize}
      style={rootStyleOverride}
      yOffset={isFull ? "0" : is90vw ? "5vh" : undefined}
      xOffset={isFull ? "0" : is90vw ? "5vw" : undefined}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      trapFocus
      returnFocus
      title={title}
      aria-labelledby={title ? "modal-title" : undefined}
      aria-describedby={description ? "modal-description" : undefined}
      transitionProps={{
        transition: "fade",
        duration: 200,
        timingFunction: "ease-in-out"
      }}
      lockScroll
      withCloseButton={showCloseButton}
      classNames={{
        content: classNames.content,
        header: headerClassNames,
        body: "flex flex-col flex-1 min-h-0",
      }}
    >
      <ResponsiveModalContent
        title={title}
        description={description}
        error={error}
        headerContent={headerContent}
        showLoadingOverlay={showLoadingOverlay}
        loading={loading}
        loadingComponent={loadingComponent}
        stickySubmit={stickySubmit}
      >
        {children}
      </ResponsiveModalContent>
    </Modal>
  );
};

// Export a hook for easier usage with state management
export const useResponsiveModal = (defaultOpen = false) => {
  const [open, setOpen] = React.useState(defaultOpen);
  
  const openModal = React.useCallback(() => setOpen(true), []);
  const closeModal = React.useCallback(() => setOpen(false), []);
  const toggleModal = React.useCallback(() => setOpen(prev => !prev), []);
  
  return {
    open,
    openModal,
    closeModal,
    toggleModal,
    setOpen,
  };
};
